/**
 * Cookie and Local Storage Management Utility
 * Handles GDPR/CCPA compliant cookie and storage management
 */

import PrivacyConsentService from '../services/PrivacyConsentService';

export interface CookieOptions {
  expires?: Date | number;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  httpOnly?: boolean;
}

export enum CookieCategory {
  ESSENTIAL = 'essential',
  ANALYTICS = 'analytics',
  ADVERTISING = 'advertising',
  FUNCTIONAL = 'functional'
}

class CookieManager {
  private static instance: CookieManager;
  private privacyService: typeof PrivacyConsentService;

  private constructor() {
    this.privacyService = PrivacyConsentService;
  }

  public static getInstance(): CookieManager {
    if (!CookieManager.instance) {
      CookieManager.instance = new CookieManager();
    }
    return CookieManager.instance;
  }

  /**
   * Set a cookie with privacy compliance checks
   */
  public setCookie(
    name: string,
    value: string,
    category: CookieCategory,
    options: CookieOptions = {}
  ): boolean {
    // Check if we can set this type of cookie
    if (!this.canSetCookie(category)) {
      console.warn(`Cannot set ${category} cookie '${name}' - user consent not given`);
      return false;
    }

    // Set default options
    const defaultOptions: CookieOptions = {
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'lax',
      expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000) // 1 year default
    };

    const finalOptions = { ...defaultOptions, ...options };

    // Build cookie string
    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (finalOptions.expires) {
      const expiresDate = typeof finalOptions.expires === 'number' 
        ? new Date(finalOptions.expires)
        : finalOptions.expires;
      cookieString += `; expires=${expiresDate.toUTCString()}`;
    }

    if (finalOptions.path) {
      cookieString += `; path=${finalOptions.path}`;
    }

    if (finalOptions.domain) {
      cookieString += `; domain=${finalOptions.domain}`;
    }

    if (finalOptions.secure) {
      cookieString += `; secure`;
    }

    if (finalOptions.sameSite) {
      cookieString += `; samesite=${finalOptions.sameSite}`;
    }

    // Note: httpOnly cannot be set via JavaScript
    if (finalOptions.httpOnly) {
      console.warn('httpOnly flag cannot be set via JavaScript');
    }

    try {
      document.cookie = cookieString;
      return true;
    } catch (error) {
      console.error('Failed to set cookie:', error);
      return false;
    }
  }

  /**
   * Get a cookie value
   */
  public getCookie(name: string): string | null {
    const nameEQ = encodeURIComponent(name) + '=';
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
      let c = cookie.trim();
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length));
      }
    }

    return null;
  }

  /**
   * Delete a cookie
   */
  public deleteCookie(name: string, path: string = '/', domain?: string): void {
    let cookieString = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
    
    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    document.cookie = cookieString;
  }

  /**
   * Check if we can set a specific category of cookie
   */
  private canSetCookie(category: CookieCategory): boolean {
    const consent = this.privacyService.getConsent();
    
    if (!consent) {
      return category === CookieCategory.ESSENTIAL;
    }

    switch (category) {
      case CookieCategory.ESSENTIAL:
        return true; // Essential cookies always allowed
      case CookieCategory.FUNCTIONAL:
        return consent.functional;
      case CookieCategory.ANALYTICS:
        return consent.analytics;
      case CookieCategory.ADVERTISING:
        return consent.marketing;
      default:
        return false;
    }
  }

  /**
   * Set item in localStorage with privacy compliance
   */
  public setLocalStorage(key: string, value: string, category: CookieCategory): boolean {
    if (!this.canSetCookie(category)) {
      console.warn(`Cannot set ${category} localStorage item '${key}' - user consent not given`);
      return false;
    }

    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.error('Failed to set localStorage item:', error);
      return false;
    }
  }

  /**
   * Get item from localStorage
   */
  public getLocalStorage(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error('Failed to get localStorage item:', error);
      return null;
    }
  }

  /**
   * Remove item from localStorage
   */
  public removeLocalStorage(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to remove localStorage item:', error);
    }
  }

  /**
   * Set item in sessionStorage with privacy compliance
   */
  public setSessionStorage(key: string, value: string, category: CookieCategory): boolean {
    if (!this.canSetCookie(category)) {
      console.warn(`Cannot set ${category} sessionStorage item '${key}' - user consent not given`);
      return false;
    }

    try {
      sessionStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.error('Failed to set sessionStorage item:', error);
      return false;
    }
  }

  /**
   * Get item from sessionStorage
   */
  public getSessionStorage(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      console.error('Failed to get sessionStorage item:', error);
      return null;
    }
  }

  /**
   * Remove item from sessionStorage
   */
  public removeSessionStorage(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to remove sessionStorage item:', error);
    }
  }

  /**
   * Clear all cookies of a specific category
   */
  public clearCookiesByCategory(category: CookieCategory): void {
    // Full implementation with category tracking
    // Track which cookies belong to which category
    const allCookies = document.cookie.split(';');
    
    for (let cookie of allCookies) {
      const name = cookie.split('=')[0].trim();
      
      // Skip essential cookies
      if (category !== CookieCategory.ESSENTIAL && this.isEssentialCookie(name)) {
        continue;
      }

      this.deleteCookie(name);
    }
  }

  /**
   * Check if a cookie is essential
   */
  private isEssentialCookie(name: string): boolean {
    const essentialCookies = [
      'fashion_app_consent',
      'fashion_app_privacy_settings',
      'fashion_app_session',
      'JSESSIONID',
      'PHPSESSID'
    ];

    return essentialCookies.some(essential => name.includes(essential));
  }

  /**
   * Clear all non-essential storage based on consent
   */
  public clearNonConsentedStorage(): void {
    const consent = this.privacyService.getConsent();
    
    if (!consent) {
      this.clearCookiesByCategory(CookieCategory.ANALYTICS);
      this.clearCookiesByCategory(CookieCategory.ADVERTISING);
      this.clearCookiesByCategory(CookieCategory.FUNCTIONAL);
      return;
    }

    if (!consent.analytics) {
      this.clearCookiesByCategory(CookieCategory.ANALYTICS);
      // Clear analytics localStorage items
      this.clearLocalStorageByPattern(['analytics', 'tracking', 'ga', 'gtm']);
    }

    if (!consent.marketing) {
      this.clearCookiesByCategory(CookieCategory.ADVERTISING);
      // Clear advertising localStorage items
      this.clearLocalStorageByPattern(['ads', 'advertising', 'targeting', 'prebid']);
    }

    if (!consent.functional) {
      this.clearCookiesByCategory(CookieCategory.FUNCTIONAL);
      // Clear functional localStorage items (except essential ones)
      this.clearLocalStorageByPattern(['preferences', 'settings'], ['fashion_app_']);
    }
  }

  /**
   * Clear localStorage items by pattern
   */
  private clearLocalStorageByPattern(patterns: string[], exceptions: string[] = []): void {
    const keys = Object.keys(localStorage);
    
    for (const key of keys) {
      // Skip if key matches any exception
      if (exceptions.some(exception => key.includes(exception))) {
        continue;
      }

      // Remove if key matches any pattern
      if (patterns.some(pattern => key.toLowerCase().includes(pattern.toLowerCase()))) {
        this.removeLocalStorage(key);
      }
    }
  }

  /**
   * Get all cookies as an object
   */
  public getAllCookies(): Record<string, string> {
    const cookies: Record<string, string> = {};
    const cookieString = document.cookie;

    if (cookieString) {
      cookieString.split(';').forEach(cookie => {
        const [name, value] = cookie.trim().split('=');
        if (name && value) {
          cookies[decodeURIComponent(name)] = decodeURIComponent(value);
        }
      });
    }

    return cookies;
  }

  /**
   * Check if cookies are enabled
   */
  public areCookiesEnabled(): boolean {
    try {
      const testCookie = 'test_cookie';
      this.setCookie(testCookie, 'test', CookieCategory.ESSENTIAL);
      const isEnabled = this.getCookie(testCookie) === 'test';
      this.deleteCookie(testCookie);
      return isEnabled;
    } catch {
      return false;
    }
  }

  /**
   * Get storage usage information
   */
  public getStorageInfo(): {
    localStorage: { used: number; available: number };
    sessionStorage: { used: number; available: number };
    cookies: { count: number; size: number };
  } {
    const getStorageSize = (storage: Storage): number => {
      let size = 0;
      for (const key in storage) {
        if (storage.hasOwnProperty(key)) {
          size += storage[key].length + key.length;
        }
      }
      return size;
    };

    const cookies = this.getAllCookies();
    const cookieSize = Object.entries(cookies).reduce(
      (size, [key, value]) => size + key.length + value.length,
      0
    );

    return {
      localStorage: {
        used: getStorageSize(localStorage),
        available: 5 * 1024 * 1024 // 5MB typical limit
      },
      sessionStorage: {
        used: getStorageSize(sessionStorage),
        available: 5 * 1024 * 1024 // 5MB typical limit
      },
      cookies: {
        count: Object.keys(cookies).length,
        size: cookieSize
      }
    };
  }
}

export default CookieManager;